import {
  calculateDispositionForCase,
  type Context,
  type DispositionResult,
  getSupportedWorkflowTypes,
  UnknownWorkflowError,
} from '@100mslive/disposition-engine';
import { useMemo } from 'react';

import { INTERNAL_AGENT_ID_KEY } from '@/lib/disposition/agentId';
import { resolveEngineWorkflowType } from '@/lib/disposition/engineWorkflowType';
import { projectTargetDisposition } from '@/lib/disposition/projectTargetDisposition';
import { inputSitesOfCare, SITES_OF_CARE_INPUT_KEY } from '@/lib/disposition/sitesOfCare';

export interface DispositionHelperResult {
  /** The workflow_type is registered with the engine. False → panel should not render. */
  supported: boolean;
  /** Disposition the engine produces from the currently committed fields. */
  current: DispositionResult | null;
  /**
   * Disposition the engine projects when missing fields get plausible best-case
   * placeholder values. Only differs from `current` when there are missing fields
   * AND filling them changes the matched scenario.
   */
  target: DispositionResult | null;
  /** Field keys (engine-side) that still need to be asked. */
  missingFields: string[];
}

const SUPPORTED_WORKFLOW_TYPES: ReadonlySet<string> = new Set(getSupportedWorkflowTypes());

/**
 * Combined engine context: case input (seed) values overlaid with the committed
 * output — `{ ...inputData, ...committedFields }`. Output wins on key collisions
 * (it's the value captured/edited during the call); input fills the gaps.
 *
 * `agentId` (the case workflow's agent UUID) is injected LAST under
 * {@link INTERNAL_AGENT_ID_KEY} so case data can never shadow it. The engine resolves
 * program flags — and therefore every program-flag-gated required field — solely from
 * that key; omitting it makes all flags resolve `false` (BI-1795). Injected only when
 * truthy, so an unknown agent keeps the engine's existing fail-open behaviour.
 *
 * The case's INPUT `sites_of_care` array is injected last too, under
 * {@link SITES_OF_CARE_INPUT_KEY}: the committed output carries its own `sites_of_care`
 * (the Output array, which has no `npi` / `tax_id`) and would otherwise shadow it, so
 * the engine could not tell an unverifiable name-only site from an identified one and
 * would keep requiring a status for both (BV-2028). Injected only when the case
 * actually has sites, so a case without them keeps the engine's fail-open behaviour.
 *
 * Exported (and pure) so the context shape can be unit-tested without rendering the hook.
 */
export function buildEngineContext(
  committedFields: Record<string, unknown>,
  inputData: Record<string, unknown> | null | undefined,
  agentId?: string,
): Record<string, unknown> {
  const sitesOfCare = inputSitesOfCare(inputData);
  return {
    ...(inputData ?? {}),
    ...committedFields,
    ...(agentId ? { [INTERNAL_AGENT_ID_KEY]: agentId } : {}),
    ...(sitesOfCare ? { [SITES_OF_CARE_INPUT_KEY]: sitesOfCare } : {}),
  };
}

/**
 * Runs the disposition engine against a snapshot of committed fields and returns
 * the current disposition, the projected target disposition, and the list of
 * fields the agent should ask next.
 *
 * Re-evaluates only when `committedFields` changes — never on in-flight keystrokes.
 * The caller is responsible for passing only committed values (e.g. snapshot on
 * field blur for text inputs; for radio/select/date the change handlers are
 * already commit events).
 *
 * The engine is fed a COMBINED context of the case's `inputData` (the seed
 * values on the case) overlaid with the committed `outputData` — i.e.
 * `{ ...inputData, ...committedFields }` — plus the case workflow's `agentId`, which the
 * engine needs to resolve program flags (see {@link buildEngineContext}). Output wins on
 * key collisions because it is the value captured/edited during the call; input only fills
 * gaps the output hasn't set. Both the current-disposition and target-projection engine
 * calls use this same combined context.
 */
export function useDispositionHelper(
  workflowType: string | undefined,
  committedFields: Record<string, unknown> | null | undefined,
  inputData?: Record<string, unknown> | null,
  agentId?: string,
): DispositionHelperResult {
  return useMemo<DispositionHelperResult>(() => {
    const engineWorkflowType = resolveEngineWorkflowType(workflowType);
    if (!engineWorkflowType || !SUPPORTED_WORKFLOW_TYPES.has(engineWorkflowType)) {
      return { supported: false, current: null, target: null, missingFields: [] };
    }
    if (!committedFields) {
      return { supported: true, current: null, target: null, missingFields: [] };
    }

    const combinedFields = buildEngineContext(committedFields, inputData, agentId);

    let current: DispositionResult | null = null;
    try {
      current = calculateDispositionForCase(engineWorkflowType, combinedFields);
    } catch (err) {
      // Engine throws TypeError for malformed input or UnknownWorkflowError if
      // the workflow_type is unregistered (already filtered above). Either way,
      // fail closed: act as unsupported rather than blowing up the UI.
      if (!(err instanceof UnknownWorkflowError) && !(err instanceof TypeError)) {
        throw err;
      }
      return { supported: false, current: null, target: null, missingFields: [] };
    }

    const missingFields = current.missing_fields ?? [];

    let target: DispositionResult | null = current;
    if (missingFields.length > 0) {
      try {
        target = projectTargetDisposition(
          engineWorkflowType,
          combinedFields as Context,
          missingFields,
        );
      } catch {
        target = current;
      }
    }

    return { supported: true, current, target, missingFields };
  }, [workflowType, committedFields, inputData, agentId]);
}
